import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import type {
  DriverDbConfirmation,
  DriverValues,
} from '../../../hooks/useDriverDatabaseDialog';
import * as S from '../../../styles';
import {
  BUTTONS,
  DIALOGS,
  DRIVER_FIELDS,
  mainColumnHeaders,
} from '../../../TransportCloudTable.consts';

const FIELD_LABEL: Record<keyof DriverValues, string> = {
  driverName: mainColumnHeaders.driverName,
  driverDocumentNumber: mainColumnHeaders.driverDocumentNumber,
  driverPhone: mainColumnHeaders.driverPhone,
};

type Props = Pick<
  DriverDbConfirmation,
  'mode' | 'values' | 'existingValues' | 'itemId' | 'columnKey'
> & {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export const DriverDatabaseConfirmDialog: React.FC<Props> = ({
  open,
  mode,
  values,
  existingValues,
  itemId,
  columnKey,
  onConfirm,
  onCancel,
}) => {
  const [currentPosition, setCurrentPosition] = useState({ top: 0, left: 0 });
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsVisible(false);
      return;
    }

    const updatePosition = () => {
      const cell = document.querySelector(
        `[data-item-id="${itemId}"][data-column="${columnKey}"]`,
      ) as HTMLElement | null;
      if (cell) {
        const rect = cell.getBoundingClientRect();
        const dialogWidth = 340;
        const leftFromCell = rect.left + window.scrollX;
        const leftAligned =
          leftFromCell + dialogWidth > window.innerWidth
            ? rect.right + window.scrollX - dialogWidth
            : leftFromCell;
        setCurrentPosition({
          top: rect.bottom + window.scrollY + 5,
          left: leftAligned,
        });
      }
      setIsVisible(true);
    };

    updatePosition();

    const scrollContainer = document.querySelector('.MuiTableContainer-root');
    scrollContainer?.addEventListener('scroll', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      scrollContainer?.removeEventListener('scroll', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [open, itemId, columnKey]);

  if (!open) return null;

  const title =
    mode === 'add' ? DIALOGS.driverAddConfirm : DIALOGS.driverEditConfirm;

  return (
    <>
      <S.ModalBackdrop onClick={onCancel} />
      <S.ModalPaper
        sx={{
          top: `${currentPosition.top}px`,
          left: `${currentPosition.left}px`,
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.1s ease-in',
          width: '340px',
        }}
      >
        <S.ModalContent>
          <Box sx={{ fontSize: '0.82rem', fontWeight: 500, mb: 1 }}>
            {title}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr',
              gap: '3px 8px',
              fontSize: '0.78rem',
            }}
          >
            {DRIVER_FIELDS.map((field) => {
              const oldVal = existingValues?.[field];
              const newVal = values[field];
              const changed = mode === 'update' && oldVal !== newVal;
              return (
                <React.Fragment key={field}>
                  <Box sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                    {FIELD_LABEL[field]}:
                  </Box>
                  <Box>
                    {changed ? (
                      <>
                        <Box
                          component='span'
                          sx={{
                            textDecoration: 'line-through',
                            color: 'text.disabled',
                            mr: 0.5,
                          }}
                        >
                          {oldVal}
                        </Box>
                        {'→ '}
                        <Box component='span' sx={{ fontWeight: 500 }}>
                          {newVal}
                        </Box>
                      </>
                    ) : (
                      newVal
                    )}
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        </S.ModalContent>
        <S.ModalActions>
          <S.TCStyledButton $primary onClick={onConfirm}>
            {BUTTONS.yes}
          </S.TCStyledButton>
          <S.TCStyledButton onClick={onCancel}>{BUTTONS.no}</S.TCStyledButton>
        </S.ModalActions>
      </S.ModalPaper>
    </>
  );
};
